import React, { useEffect } from 'react';
import Theme from '@amzn/meridian/theme';
import { useAppDispatch, useAppSelector } from '../store';
import { setDarkModeFromParent, initializeTheme } from '../store/slices/themeSlice';
import { setDarkModeStateListener, getDarkModeStatus } from '../services/boson/bosonClient';
import { DarkModeValue } from '../utils/constants';
import customTheme from '../themes/customTheme.json';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { currentMode, darkModeFromParent, isThemeInitialized } = useAppSelector((state) => state.theme);

  useEffect(() => {
    // Set up dark mode listener for parent communication
    setDarkModeStateListener((darkMode: DarkModeValue) => {
      dispatch(setDarkModeFromParent(darkMode));
    });

    // Initialize theme and get initial dark mode status from parent
    const initializeThemeState = async () => {
      try {
        const parentDarkModeResponse = await getDarkModeStatus();
        // Parent responds with data.childData.capability = "DARK" or "LIGHT"
        // The listener (setDarkModeStateListener) already handles this,
        // but also handle it here for the initial getDarkModeStatus() response
        if (parentDarkModeResponse && parentDarkModeResponse.childData && parentDarkModeResponse.childData.capability) {
          // Case-neutral comparison for parent dark mode capability
          const isDark = parentDarkModeResponse.childData.capability?.toString().toUpperCase() === 'DARK';
          dispatch(setDarkModeFromParent(isDark ? 'DARK' : 'LIGHT'));
        } else if (parentDarkModeResponse && parentDarkModeResponse.args && parentDarkModeResponse.args.darkMode) {
          // Fallback for alternative response format - case-neutral
          const isDark = parentDarkModeResponse.args.darkMode?.toString().toLowerCase() === 'dark';
          dispatch(setDarkModeFromParent(isDark ? 'DARK' : 'LIGHT'));
        }
      } catch (error) {
        console.warn('Could not get dark mode status from parent:', error);
        // Default to light mode if parent communication fails
        dispatch(setDarkModeFromParent('LIGHT'));
      } finally {
        dispatch(initializeTheme());
      }
    };

    if (!isThemeInitialized) {
      initializeThemeState();
    }
  }, [dispatch, isThemeInitialized]);

  // Determine the actual theme mode to use
  const getThemeMode = (): "light" | "dark" => {
    if (currentMode === "auto") {
      // Use parent's dark mode preference when in auto mode
      return darkModeFromParent === 'DARK' ? "dark" : "light";
    }
    return currentMode === "dark" ? "dark" : "light";
  };

  const themeMode = getThemeMode();

  // Set data attribute on document root for CSS selectors
  useEffect(() => {
    document.documentElement.setAttribute('data-theme-mode', themeMode);
  }, [themeMode]);

  return (
    <Theme
      palette={customTheme}
      mode={themeMode}
    >
      {children}
    </Theme>
  );
};

export default ThemeProvider;